import React, { useState } from 'react';

import { useParams, useSearchParams } from 'react-router-dom';

import EditCalendarIcon from '@mui/icons-material/EditCalendar';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Container,
  Grid,
  IconButton,
  Paper,
  Stack,
  Step,
  StepButton,
  StepLabel,
  Stepper,
  Tooltip,
  Typography,
} from '@mui/material';

import PageHeader from '~/components/Common/Elements/PageHeader';

import { useGetBundle } from '~/hooks/apis/bundle/useBundles';

import { BUNDLE_STATUS_LABELS, Bundle } from '~/types/bundles/bundle';

import BuilderStepLayout from '../../Bundles/BuilderStepLayout';
import BundleApprovalStep from '../../Bundles/BundleApprovalStep';
import BundleContentStep from '../../Bundles/BundleContentStep';
import BundleDepartureChecks from '../../Bundles/BundleDepartureChecks';
import BundleItinerary from '../../Bundles/BundleItinerary';
import BundleMarkets from '../../Bundles/BundleMarkets';
import BundleOverview from '../../Bundles/BundleOverview';
import BundlePricesStep from '../../Bundles/BundlePricesStep';
import RenameBundleDialog from '../../Bundles/RenameBundleDialog';
import TourComponentDialog from '../../Bundles/TourComponentDialog';
import useBundleReadiness, { BUILDER_STEPS, BuilderStep } from '../../Bundles/useBundleReadiness';
import { BUNDLES_PATH } from '../../nativeTourModel';

const STEP_PARAM = 'step';

const isBuilderStep = (value: string | null): value is BuilderStep => BUILDER_STEPS.some((step) => step.key === value);

function LockedStep({ message, action }: { message: string; action: React.ReactNode }) {
  return (
    <Paper variant="outlined" sx={{ borderStyle: 'dashed', p: 5, textAlign: 'center' }}>
      <Stack spacing={2} alignItems="center">
        <LockOutlinedIcon color="disabled" fontSize="large" />
        <Typography color="text.secondary" maxWidth={480}>
          {message}
        </Typography>
        {action}
      </Stack>
    </Paper>
  );
}

function BundleBuilder({ bundle }: { bundle: Bundle }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isDeparturesOpen, setIsDeparturesOpen] = useState(false);
  const readiness = useBundleReadiness(bundle);
  const { tour, departures, blocked, checks, blockers, markets, isApproved, hasLtle, isStepComplete, nextStep } =
    readiness;
  const hasNowhereToSell =
    markets.hasTour && markets.hasHotel && !markets.isLoading && !markets.error && markets.sellsIn.length === 0;

  // The step lives in the URL so a reload or a shared link opens the same place.
  const requested = searchParams.get(STEP_PARAM);
  const activeStep: BuilderStep = isBuilderStep(requested) ? requested : nextStep;
  const activeIndex = BUILDER_STEPS.findIndex((step) => step.key === activeStep);
  const isDraft = bundle.status === 'draft';

  const goToStep = (step: BuilderStep) => {
    setSearchParams(
      (params) => {
        params.set(STEP_PARAM, step);
        return params;
      },
      { replace: false },
    );
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const stepNote: Record<BuilderStep, React.ReactNode> = {
    build: isStepComplete.build ? 'Ready' : 'Tour and hotel',
    departures: hasNowhereToSell
      ? 'Nowhere to sell'
      : departures.length === 0
        ? 'None chosen'
        : blocked.length > 0
          ? `${blocked.length} can't be sold`
          : `${departures.length} chosen`,
    prices: 'Adjust per departure',
    approval: isApproved ? 'Approved' : blockers.length > 0 ? `${blockers.length} to do` : 'Ready to approve',
    content: hasLtle ? 'On the LTLE' : 'After approval',
  };

  const isWide = activeStep === 'prices';

  return (
    <Stack spacing={3}>
      <Card variant="outlined" sx={{ px: 2, py: 2.5, overflowX: 'auto' }}>
        <Stepper nonLinear activeStep={activeIndex} sx={{ minWidth: 640 }}>
          {BUILDER_STEPS.map((step) => {
            const isError = step.key === 'departures' && (blocked.length > 0 || hasNowhereToSell);
            return (
              <Step key={step.key} completed={isStepComplete[step.key]} disabled={step.key === 'content' && !hasLtle}>
                <StepButton onClick={() => goToStep(step.key)}>
                  <StepLabel
                    error={isError}
                    optional={
                      <Typography variant="caption" color={isError ? 'error' : 'text.secondary'}>
                        {stepNote[step.key]}
                      </Typography>
                    }
                  >
                    {step.label}
                  </StepLabel>
                </StepButton>
              </Step>
            );
          })}
        </Stepper>
      </Card>

      <Grid container spacing={3} alignItems="flex-start">
        {/* The prices table needs the full width; the step itself covers what the summary would show. */}
        <Grid size={{ xs: 12, md: isWide ? 12 : 8 }}>
          {activeStep === 'build' && (
            <BuilderStepLayout
              title="Tour & hotel"
              description="Pick the base tour and pair it with a flash hotel on sale where the tour is scheduled."
              next={{ label: 'Continue to departures', onClick: () => goToStep('departures'), disabled: !tour }}
            >
              <BundleItinerary bundle={bundle} />
            </BuilderStepLayout>
          )}

          {activeStep === 'departures' && (
            <BuilderStepLayout
              title="Departures & markets"
              description="The tour's departures this bundle sells, each checked for a single and a double, and the regions and brands it sells under."
              actions={
                isDraft &&
                !!tour &&
                departures.length > 0 && (
                  <Button startIcon={<EditCalendarIcon />} onClick={() => setIsDeparturesOpen(true)}>
                    Edit departures
                  </Button>
                )
              }
              back={{ label: 'Tour & hotel', onClick: () => goToStep('build') }}
              next={{
                label: 'Continue to prices',
                onClick: () => goToStep('prices'),
                disabled: departures.length === 0,
              }}
            >
              {!tour ? (
                <LockedStep
                  message="Add the base tour first. Its departures are the ones the bundle can sell."
                  action={<Button onClick={() => goToStep('build')}>Go to tour & hotel</Button>}
                />
              ) : departures.length === 0 ? (
                <Paper variant="outlined" sx={{ borderStyle: 'dashed', p: 5, textAlign: 'center' }}>
                  <Stack spacing={2} alignItems="center">
                    <EditCalendarIcon color="primary" fontSize="large" />
                    <Box>
                      <Typography variant="h6">No departures chosen yet</Typography>
                      <Typography color="text.secondary">
                        Choose which of the tour's departures this bundle sells.
                      </Typography>
                    </Box>
                    {isDraft && (
                      <Button variant="contained" onClick={() => setIsDeparturesOpen(true)}>
                        Choose departures
                      </Button>
                    )}
                  </Stack>
                </Paper>
              ) : (
                <BundleDepartureChecks bundle={bundle} />
              )}
              <BundleMarkets bundle={bundle} />
              {!!tour && (
                <TourComponentDialog
                  bundle={bundle}
                  component={tour}
                  open={isDeparturesOpen}
                  onClose={() => setIsDeparturesOpen(false)}
                />
              )}
            </BuilderStepLayout>
          )}

          {activeStep === 'prices' && (
            <BuilderStepLayout
              title="Prices"
              description="Set a surcharge or discount on the tour and the hotel for each departure, and see what the bundle sells for."
              back={{ label: 'Departures & markets', onClick: () => goToStep('departures') }}
              next={{ label: 'Continue to approval', onClick: () => goToStep('approval') }}
            >
              <BundlePricesStep bundle={bundle} />
            </BuilderStepLayout>
          )}

          {activeStep === 'approval' && (
            <BuilderStepLayout
              title="Approval"
              description={
                isApproved
                  ? 'The tour and hotel are paired and the bundle has its LTLE.'
                  : 'Check everything is in place, then approve the pairing to create the bundle’s LTLE.'
              }
              back={{ label: 'Prices', onClick: () => goToStep('prices') }}
              next={isApproved ? { label: 'Continue to content', onClick: () => goToStep('content') } : undefined}
            >
              <BundleApprovalStep bundle={bundle} checks={checks} blockers={blockers} onGoToStep={goToStep} />
            </BuilderStepLayout>
          )}

          {activeStep === 'content' && (
            <BuilderStepLayout
              title="Content"
              description="What customers see on the offer page. It starts as a copy of the base tour on the bundle’s LTLE, and edits change this bundle only."
              back={{ label: 'Approval', onClick: () => goToStep('approval') }}
            >
              {hasLtle ? (
                <BundleContentStep bundle={bundle} />
              ) : (
                <LockedStep
                  message="Content is edited on the bundle's LTLE, which is created when the bundle is approved."
                  action={
                    <Button variant="contained" onClick={() => goToStep('approval')}>
                      Go to approval
                    </Button>
                  }
                />
              )}
            </BuilderStepLayout>
          )}
        </Grid>
        {!isWide && (
          <Grid size={{ xs: 12, md: 4 }}>
            <BundleOverview readiness={readiness} activeStep={activeStep} onGoToStep={goToStep} />
          </Grid>
        )}
      </Grid>
    </Stack>
  );
}

export default function TourBundlePage() {
  const { bundle_id: bundleId } = useParams<{ bundle_id: string }>();
  const [isRenameOpen, setIsRenameOpen] = useState(false);
  const { data: bundle, isLoading, error } = useGetBundle(bundleId);

  return (
    <Container maxWidth="xl">
      <PageHeader
        title={bundle?.name ?? 'Bundle'}
        backButton={BUNDLES_PATH}
        titleActions={
          bundle && (
            <Stack direction="row" spacing={1} alignItems="center">
              <Tooltip title="Rename">
                <IconButton size="small" aria-label="Rename bundle" onClick={() => setIsRenameOpen(true)}>
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Chip
                size="small"
                label={BUNDLE_STATUS_LABELS[bundle.status]}
                color={bundle.status === 'approved' ? 'success' : 'default'}
              />
            </Stack>
          )
        }
      />

      {isLoading && (
        <Box p={4} display="flex" justifyContent="center">
          <CircularProgress />
        </Box>
      )}

      {!!error && <Alert severity="error">{error.message}</Alert>}

      {!!bundle && (
        <>
          <BundleBuilder bundle={bundle} />
          <RenameBundleDialog bundle={bundle} open={isRenameOpen} onClose={() => setIsRenameOpen(false)} />
        </>
      )}
    </Container>
  );
}
